import React, { useRef, useEffect } from "react";
import { Box } from "@mui/material";

/**
 * Masonry and Grid layout switchable BentoBoxGrid
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {number} [props.gap=2] - gap in theme spacing units (8px)
 * @param {number} [props.column=2] - number of columns
 * @param {boolean} [props.masonry=false] - if true, use Masonry (CSS columns) layout
 * @param {number} [props.ROW_HEIGHT=5.5] - grid row height (only for grid mode)
 * @param {object} [props.sx] - extra styles
 */
const BentoBoxGrid = ({
  children,
  gap = 2,
  column = 2,
  masonry = true,
  ROW_HEIGHT = 5.5,
  sx = {},
  ...rest
}) => {
  const gridRef = useRef(null);

  // Only run rowSpan logic in grid mode
  useEffect(() => {
    if (masonry) return;
    const recalcRowSpans = () => {
      const grid = gridRef.current;
      if (!grid) return;
      const items = grid.querySelectorAll(".bento-item-inner");
      items.forEach((item) => {
        const wrapper = item.parentElement;
        wrapper.style.gridRow = "auto";
        const height = item.getBoundingClientRect().height;
        const gapPx = gap * 8;
        const rowSpan = Math.ceil((height + gapPx) / (ROW_HEIGHT + gapPx));
        wrapper.style.gridRow = `span ${rowSpan}`;
      });
    };
    recalcRowSpans();
    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(recalcRowSpans);
    });
    const items = gridRef.current?.querySelectorAll(".bento-item-inner");
    items?.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, [children, masonry, gap, ROW_HEIGHT]);

  if (masonry) {
    // CSS columns Masonry layout
    return (
      <Box
        ref={gridRef}
        sx={{
          columnCount: column,
          columnGap: `${gap * 8}px`,
          width: '100%',
          ...sx,
        }}
        {...rest}
      >
        {React.Children.map(children, child =>
          child ? (
            <Box
              sx={{
                breakInside: 'avoid',
                mb: `${gap * 8}px`, // Always apply bottom margin for consistent spacing
                width: '100%'
              }}
            >
              {child}
            </Box>
          ) : null
        )}
      </Box>
    );
  }

  // Default: CSS Grid
  return (
    <Box
      ref={gridRef}
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${column}, 1fr)`,
        gridAutoRows: `${ROW_HEIGHT}px`,
        gap: `${gap * 8}px`,
        alignItems: "start",
        gridAutoFlow: "dense",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

export default BentoBoxGrid;
